import { AxiosError } from 'axios';
import { Field, Formik } from 'formik';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import {
  Form,
  FormGroup,
  TextInput,
  Stack,
  FormItem,
  Button,
  Toggle,
  TextInputSkeleton,
  InlineNotification,
  InlineLoading,
  Select,
  SelectItem,
} from '@carbon/react';
import { CurrencyEnum } from '@restavo/shared/enums/currency';
import CurrencySelect from 'src/components/Form/CurrencySelect';
import { FormikTextInput } from 'src/components/Form/FormikInputs';
import DndUploader from 'src/components/Uploader/DNDUploader';
import { useCurrentRestaurant, useUpdateRestaurantMutation } from 'src/hooks/useRestaurants';
import { getImageUrl } from 'src/utils/images';
import { MAX_PAYROLL_HOURS_PER_MONTH, REPORTING_TIMEZONES, timezoneLabel } from 'src/utils/timezones';

function FormLoading() {
  return (
    <>
      <TextInputSkeleton />
      <TextInputSkeleton />
      <TextInputSkeleton />
      <TextInputSkeleton />
    </>
  );
}

const Schema = z.object({
  name: z.string().max(60).trim(),
  slug: z.string().max(24).trim(),
  slogan: z.string().max(255).trim(),
  address: z.string().max(255).trim(),
  latitude: z.preprocess((a) => parseFloat(`${a}`), z.number()),
  longitude: z.preprocess((a) => parseFloat(`${a}`), z.number()),
  currency: z.nativeEnum(CurrencyEnum),
  reportingTimezone: z.string().min(1),
  payrollHoursPerMonth: z.preprocess(
    (a) => parseInt(`${a}`, 10),
    z.number().int().min(1).max(MAX_PAYROLL_HOURS_PER_MONTH)
  ),
});

function FormField(props: { label: string; name: string }) {
  const { name, label } = props;
  return (
    <Field name={name}>
      {({ field, meta }) => {
        return (
          <TextInput
            id={name}
            labelText={label}
            invalid={!!(meta.touched && meta.error)}
            invalidText={meta.error}
            {...field}
          />
        );
      }}
    </Field>
  );
}

function FormFieldUpload(props: {
  value: any;
  title: string;
  aspectRatio: string;
  cropperAspectRatio: number;
  description: string;
  onChange: (data) => void;
}) {
  return (
    <FormItem>
      <p className="cds--file--label">{props.title}</p>
      <p className="cds--label-description">{props.description}</p>
      <DndUploader
        useCropper
        initialValue={{ fileId: props.value, previewUrl: getImageUrl(props.value)! }}
        previewAspectRatio={props.aspectRatio}
        cropperAspectRatio={props.cropperAspectRatio}
        onChange={props.onChange}
      />
    </FormItem>
  );
}

const RestaurantInformationSettings = React.memo(() => {
  const { t } = useTranslation();
  const currentRestaurant = useCurrentRestaurant();
  const {
    error: updateRestaurantError,
    isLoading: updateRestaurantLoading,
    isSuccess: updateRestaurantSuccess,
    mutate: updateRestaurant,
    reset: updateRestaurantReset,
  } = useUpdateRestaurantMutation();

  const postHandleSubmit = useCallback(
    (values) => {
      updateRestaurant({
        ...values,
        latitude: parseFloat(values.latitude),
        longitude: parseFloat(values.longitude),
        payrollHoursPerMonth: parseInt(`${values.payrollHoursPerMonth}`, 10),
      });
    },
    [updateRestaurant]
  );

  useEffect(() => {
    setTimeout(() => {
      updateRestaurantReset();
    }, 1500);
  }, [updateRestaurantSuccess, updateRestaurantReset]);

  const error = updateRestaurantError as AxiosError;
  let errorMessage = error && error?.response?.status !== 200 ? 'Something wrong!' : '';

  return (
    // Matches Organization General: a plain Carbon Form, spaced by Stack, taking its width
    // from the settings stylesheet's `.cds--form` cap rather than an inline 400px. The
    // `store-settings-information-page` card wrapper is gone with it — that background and
    // padding were the last thing making this page look unlike its siblings.
    <Form>
      <FormGroup legendText={t('common.basicInformation')}>
        <Stack gap={6}>
          {!currentRestaurant && <FormLoading />}
          {currentRestaurant && (
            <Formik
              validationSchema={toFormikValidationSchema(Schema)}
              initialValues={{
                id: currentRestaurant.id,
                name: currentRestaurant.name,
                slug: currentRestaurant.slug,
                slogan: currentRestaurant.slogan,
                address: currentRestaurant.address,
                latitude: `${currentRestaurant.latitude}`,
                longitude: `${currentRestaurant.longitude}`,
                activated: currentRestaurant.activated,
                logo: currentRestaurant.logo,
                logoBlurhash: currentRestaurant.logoBlurhash,
                cover: currentRestaurant.cover,
                coverBlurhash: currentRestaurant.coverBlurhash,
                countryCode: currentRestaurant.countryCode,
                currency: (currentRestaurant.currency as CurrencyEnum) ?? CurrencyEnum.VND,
                reportingTimezone: currentRestaurant.reportingTimezone,
                payrollHoursPerMonth: currentRestaurant.payrollHoursPerMonth,
              }}
              onSubmit={postHandleSubmit}
            >
              {({ handleSubmit, values, setFieldValue, isValid }) => (
                <>
                  <FormField name={'name'} label={t('common.name')} />
                  <FormField name={'slug'} label={t('common.slug')} />
                  <FormField name={'address'} label={t('common.address')} />
                  <FormField name={'slogan'} label={t('common.slogan')} />
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <FormField name={'latitude'} label={t('common.latitude')} />
                    <FormField name={'longitude'} label={t('common.longitude')} />
                  </div>
                  <Select
                    id="reportingTimezone"
                    labelText={t('common.reportingTimezone')}
                    helperText={t('common.reportingTimezoneHint')}
                    value={values.reportingTimezone}
                    onChange={(e) => setFieldValue('reportingTimezone', e.target.value)}
                  >
                    {REPORTING_TIMEZONES.map((zone) => (
                      <SelectItem key={zone} value={zone} text={timezoneLabel(zone)} />
                    ))}
                  </Select>
                  <CurrencySelect
                    id="currency"
                    labelText={t('common.currency')}
                    value={values.currency}
                    onChange={(value) => setFieldValue('currency', value)}
                    warning={
                      values.currency !== currentRestaurant.currency ? t('common.currencyChangeWarning') : undefined
                    }
                  />
                  <FormikTextInput
                    name={'payrollHoursPerMonth'}
                    isNumber
                    min={1}
                    max={MAX_PAYROLL_HOURS_PER_MONTH}
                    label={t('common.payrollHoursPerMonth')}
                    helperText={t('common.payrollHoursPerMonthHint')}
                  />
                  <Toggle
                    labelText={t('common.activation')}
                    labelA={t('common.off')}
                    labelB={t('common.on')}
                    onChange={(e, { checked }) => setFieldValue('activated', checked)}
                    defaultToggled={values.activated}
                    id="activated"
                  />
                  <FormFieldUpload
                    title={t('common.restaurantLogo')}
                    description={t('messages.photoRecommended', { size: 1, aspectRatio: '1x1' })}
                    cropperAspectRatio={1}
                    aspectRatio={'1x1'}
                    value={values.logo}
                    onChange={(data) => {
                      setFieldValue('logo', data.fileId);
                      setFieldValue('logoBlurhash', data.blurhash);
                    }}
                  />
                  <FormFieldUpload
                    title={t('common.restaurantCover')}
                    description={t('messages.photoRecommended', { size: 1, aspectRatio: '16x9' })}
                    cropperAspectRatio={16 / 9}
                    aspectRatio={'16x9'}
                    value={values.cover}
                    onChange={(data) => {
                      setFieldValue('cover', data.fileId);
                      setFieldValue('coverBlurhash', data.blurhash);
                    }}
                  />
                  {errorMessage && <InlineNotification subtitle={errorMessage} />}
                  {updateRestaurantLoading || updateRestaurantSuccess ? (
                    <InlineLoading
                      description={updateRestaurantSuccess ? t('common.saved') : `${t('actions.saving')}...`}
                      status={updateRestaurantSuccess ? 'finished' : 'active'}
                    />
                  ) : (
                    <Button disabled={!isValid} onClick={handleSubmit}>
                      {t('actions.save')}
                    </Button>
                  )}
                </>
              )}
            </Formik>
          )}
        </Stack>
      </FormGroup>
    </Form>
  );
});

export default RestaurantInformationSettings;
